import type { Booking } from '@cocoonspace/sdk-js/types/booking'
import { useListBookings } from '@cocoonspace/sdk-js/domains/bookings/bookings'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@cocoonspace/ui/components/table'
import { useLingui } from '@lingui/react/macro'
import { EyeIcon } from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'

import { QuotationStatus } from '~/domains/account/components/quotations/quotations-list/quotation-status'
import { useFormatPrice } from '~/hooks/use-format-price'

const useQuotationRequestsToTreat = () => {
	return useListBookings(
		{ type: 'quotation', status: ['require_validation'], mine: true },
		{ query: { select: ({ data }) => data } },
	)
}

const QuotationRow = ({ quotation }: { quotation: Booking }) => {
	const formatPrice = useFormatPrice()

	return (
		<TableRow>
			<TableCell className='w-[110px]'>
				{quotation.created_at
					? dateTimeUtils(quotation.created_at).customFormat('date')
					: '-'}
			</TableCell>
			<TableCell className='font-medium'>{quotation.ref}</TableCell>
			<TableCell className='w-[110px]'>
				{quotation.expires_at
					? dateTimeUtils(quotation.expires_at).customFormat('date')
					: '-'}
			</TableCell>
			<TableCell className='w-[150px]'>
				<QuotationStatus status={quotation.status} />
			</TableCell>
			<TableCell className='w-[150px] text-right'>
				{formatPrice(quotation.total_price, 'EUR')}
				<span className='text-muted-foreground ml-1 text-xs'>HT</span>
			</TableCell>
			<TableCell className='w-[50px] p-0'>
				<Button
					asChild
					size='sm'
					variant='ghost'
					icon
				>
					<Link href={`/quotations/${quotation.id}`}>
						<EyeIcon />
					</Link>
				</Button>
			</TableCell>
		</TableRow>
	)
}

export const QuotationRequests = () => {
	const { t } = useLingui()

	const { data: quotations } = useQuotationRequestsToTreat()

	if (!quotations?.length) return null

	return (
		<div className='flex flex-col gap-4'>
			<h2 className='text-3xl font-bold'>{t`Mes devis à traiter`}</h2>

			<Card className='overflow-hidden'>
				<Table className='w-full'>
					<TableHeader>
						<TableRow>
							<TableHead className='w-[110px]'>{t`Créé le`}</TableHead>
							<TableHead>{t`Ref devis`}</TableHead>
							<TableHead className='w-[110px]'>{t`Expire le`}</TableHead>
							<TableHead className='w-[150px]'>{t`Statut`}</TableHead>
							<TableHead className='w-[150px] text-right'>{t`Total`}</TableHead>
							<TableHead className='w-[50px] p-0' />
						</TableRow>
					</TableHeader>
					<TableBody>
						{quotations.map((quotation) => (
							<QuotationRow
								key={quotation.id}
								quotation={quotation}
							/>
						))}
					</TableBody>
				</Table>
			</Card>
		</div>
	)
}
